'use client'
import * as React from 'react'

import { ChevronsUpDown, Search } from 'lucide-react'
import { Collapsible, CollapsibleContent } from '@radix-ui/react-collapsible'
import { cn } from '../helpers/classnames'
import { SButton } from '../button/s-button'
import { Badge } from '../badge'
import { CheckIcon, LockClosedIcon, XMarkIcon } from '@heroicons/react/16/solid'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../tabs'
import Drawer from '../main-drawer'
import SlideMenu from '../slide-menu'
import { useCoordinatedDrawer } from '../drawer-coordinator'

export type Option = {
  value: string
  name: string
  description?: string
  imageUrl?: string
  tab_key?: string
}

type TabKey = {
  id: string
  name: string
}

interface GridMultiSelectProps {
  options: Option[]
  selected?: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  className?: string
  title?: string
  brandKit?: boolean
  tab_keys?: TabKey[]
  disabled?: boolean
  classNames?: {
    selected?: string
    list_item?: string
  }
  inner?: boolean
}
export function GridMultiSelect({
  options = [],
  selected: local_selected = [],
  onChange,
  placeholder = 'Select items...',
  className,
  title = 'Select Options',
  brandKit = false,
  tab_keys = [],
  disabled = false,
  classNames,
  inner = false,
}: GridMultiSelectProps) {
  const instanceId = React.useId()
  
  // Only use coordinator for inner (SlideMenu) components
  const coordinatorId = `${title}-${instanceId}`
  const { open: coordinatedOpen, setOpen: setCoordinatedOpen } = useCoordinatedDrawer(
    inner ? coordinatorId : undefined // Only pass ID if inner=true
  )
  
  // Local state for non-coordinated drawers
  const [localOpen, setLocalOpen] = React.useState(false)
  
  // Use coordinated state only if inner=true and coordinator exists
  const open = inner && coordinatedOpen !== undefined ? coordinatedOpen : localOpen
  const setOpen = inner && coordinatedOpen !== undefined ? setCoordinatedOpen : setLocalOpen
  
  const selected = React.useMemo(() => {
    return local_selected
  }, [local_selected])

  const [showSelected, setShowSelected] = React.useState(true)
  const [searchTerm, setSearchTerm] = React.useState('')

  const selectedText =
    selected.length === 0
      ? placeholder
      : `${selected.length} item${selected.length === 1 ? '' : 's'} selected`

  const toggleOption = (value: string) => {
    const newSelected = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value]
    onChange(newSelected)
  }

  const removeSelected = (value: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(selected.filter((v) => v !== value))
  }

  const selectAll = () => {
    const filteredOptions = getFilteredOptions(options)
    const allValues = filteredOptions.map((option) => option.value)
    const newSelected = [...new Set([...selected, ...allValues])]
    onChange(newSelected)
  }

  const deselectAll = () => {
    const filteredOptions = getFilteredOptions(options)
    const filteredValues = filteredOptions.map((option) => option.value)
    const newSelected = selected.filter(
      (value) => !filteredValues.includes(value)
    )
    onChange(newSelected)
  }

  const getFilteredOptions = (optionList: Option[]) => {
    if (!searchTerm) return optionList
    return optionList.filter(
      (option) =>
        option.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (option.description &&
          option.description.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }

  const renderOptionsGrid = (filteredOptions: Option[]) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {filteredOptions.map((option) => {
        const isSelected = !!selected.find(
          (selected) => selected === option.value
        )
        return (
          <div
            key={option.value}
            className={cn(
              'relative p-3 hover:bg-gray-50 dark:hover:bg-[#252525] transition-colors cursor-pointer rounded-md border border-gray-300 dark:border-[#3a3a3a]',
              isSelected &&
                'border-pink-500 dark:border-pink-400 bg-pink-50 dark:bg-pink-900/10',
              classNames?.selected && isSelected ? classNames?.selected : ''
            )}
            onClick={() => toggleOption(option.value)}
          >
            {option.imageUrl && (
              <img
                src={option.imageUrl}
                alt={option.name}
                className="w-full h-16 object-cover rounded-md mb-2"
              />
            )}
            <div className="text-center">
              <div className="text-[12px] text-gray-900 dark:text-white font-medium truncate">
                {option.name}
              </div>
              {option.description && (
                <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                  {option.description}
                </div>
              )}
            </div>
            {isSelected && (
              <div className="absolute top-2 right-2">
                <CheckIcon className="h-3 w-3 text-pink-500 dark:text-pink-400" />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )

  return (
    <>
      {inner ? (
        <SlideMenu
          key={instanceId}
          className="w-full"
          is_open={open}
          onClose={() => setOpen(false)}
          title={title}
        >
          <div className="w-full h-full flex flex-col bg-white dark:bg-[#191919]">
            {/* Search and Controls */}
            <div className="p-4 border-b border-gray-300 dark:border-[#3a3a3a]">
              {/* Search */}
              <div className="relative mb-3">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search options..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-[12px] bg-gray-50 dark:bg-[#1a1a1a] border border-gray-300 dark:border-[#3a3a3a] rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white transition-colors"
                />
              </div>

              {/* Select All Controls */}
              <div className="flex gap-2">
                <SButton
                  variant="ghost_default"
                  size="sm"
                  onClick={selectAll}
                  className="text-[11px] h-7 px-3"
                >
                  Select All
                </SButton>
                <SButton
                  variant="ghost_default"
                  size="sm"
                  onClick={deselectAll}
                  className="text-[11px] h-7 px-3"
                >
                  Deselect All
                </SButton>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-4 overflow-y-auto">
              {tab_keys.length > 0 ? (
                <Tabs defaultValue={tab_keys[0].id} className="h-full">
                  <div className="mb-3">
                    <div className="flex gap-1 overflow-x-auto">
                      {tab_keys.map((tab) => (
                        <TabsTrigger
                          key={tab.id}
                          value={tab.id}
                          className="px-3 py-2 text-[12px] font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border-b-2 border-transparent data-[state=active]:border-pink-500 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white transition-colors whitespace-nowrap"
                        >
                          {tab.name}
                        </TabsTrigger>
                      ))}
                    </div>
                  </div>
                  {tab_keys.map((tab) => (
                    <TabsContent key={tab.id} value={tab.id} className="h-full">
                      {renderOptionsGrid(
                        getFilteredOptions(
                          options.filter((option) => option.tab_key === tab.id)
                        )
                      )}
                    </TabsContent>
                  ))}
                </Tabs>
              ) : (
                renderOptionsGrid(getFilteredOptions(options))
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-300 dark:border-[#3a3a3a] bg-gray-50 dark:bg-[#232323]">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                    Selected
                  </div>
                  <div className="text-[12px] text-gray-900 dark:text-white font-medium mt-0.5">
                    {selectedText}
                  </div>
                </div>
                <SButton
                  onClick={() => setOpen(false)}
                  className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-300 dark:border-[#3a3a3a] rounded-md transition-colors text-[11px] font-medium px-4 py-2 h-auto"
                >
                  Done
                </SButton>
              </div>
            </div>
          </div>
        </SlideMenu>
      ) : (
        <Drawer
          key={instanceId}
          open={open}
          setOpen={() => setOpen(false)}
          title={title}
        >
          <div className="w-full h-full flex flex-col bg-white dark:bg-[#191919]">
            {/* Search and Controls */}
            <div className="p-4 border-b border-gray-300 dark:border-[#3a3a3a]">
              {/* Search */}
              <div className="relative mb-3">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search options..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-[12px] bg-gray-50 dark:bg-[#1a1a1a] border border-gray-300 dark:border-[#3a3a3a] rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white transition-colors"
                />
              </div>

              {/* Select All Controls */}
              <div className="flex gap-2">
                <SButton
                  variant="ghost_default"
                  size="sm"
                  onClick={selectAll}
                  className="text-[11px] h-7 px-3"
                >
                  Select All
                </SButton>
                <SButton
                  variant="ghost_default"
                  size="sm"
                  onClick={deselectAll}
                  className="text-[11px] h-7 px-3"
                >
                  Deselect All
                </SButton>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-4 overflow-y-auto">
              {tab_keys.length > 0 ? (
                <Tabs defaultValue={tab_keys[0].id} className="h-full">
                  <div className="mb-3">
                    <div className="flex gap-1 overflow-x-auto">
                      {tab_keys.map((tab) => (
                        <TabsTrigger
                          key={tab.id}
                          value={tab.id}
                          className="px-3 py-2 text-[12px] font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border-b-2 border-transparent data-[state=active]:border-pink-500 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white transition-colors whitespace-nowrap"
                        >
                          {tab.name}
                        </TabsTrigger>
                      ))}
                    </div>
                  </div>
                  {tab_keys.map((tab) => (
                    <TabsContent key={tab.id} value={tab.id} className="h-full">
                      {renderOptionsGrid(
                        getFilteredOptions(
                          options.filter((option) => option.tab_key === tab.id)
                        )
                      )}
                    </TabsContent>
                  ))}
                </Tabs>
              ) : (
                renderOptionsGrid(getFilteredOptions(options))
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-300 dark:border-[#3a3a3a] bg-gray-50 dark:bg-[#232323]">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                    Selected
                  </div>
                  <div className="text-[12px] text-gray-900 dark:text-white font-medium mt-0.5">
                    {selectedText}
                  </div>
                </div>
                <SButton
                  onClick={() => setOpen(false)}
                  className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-300 dark:border-[#3a3a3a] rounded-md transition-colors text-[11px] font-medium px-4 py-2 h-auto"
                >
                  Done
                </SButton>
              </div>
            </div>
          </div>
        </Drawer>
      )}

      <div className="space-y-2" data-instance-id={instanceId}>
        {/* Input-like Trigger */}
        <div
          className="group cursor-pointer"
          onClick={() => {
            // @ts-ignore
            window.__debug = window.__debug || {}
            // @ts-ignore
            window.__debug[`${title}-${instanceId}`] = {
              clicked: Date.now(),
              options: options.length,
              selected: selected.length,
              instanceId,
              title,
              coordinatorId,
              inner,
              isCoordinated: inner && coordinatedOpen !== undefined,
            }
            console.log('GridMultiSelect clicked:', title, coordinatorId, {
              options: options.length,
              selected: selected.length,
              inner,
              isCoordinated: inner && coordinatedOpen !== undefined,
            })
            setOpen(true)
          }}
        >
          <div
            className={cn(
              'bg-gray-50 dark:bg-[#1a1a1a] border border-gray-300 dark:border-[#3a3a3a] rounded-md px-3 py-2 min-h-[40px] flex items-center transition-colors',
              'hover:border-gray-400 dark:hover:border-gray-500',
              disabled && 'opacity-50 cursor-not-allowed',
              open && 'ring-2 ring-pink-500 border-pink-500'
            )}
          >
            <div className="flex items-center justify-between w-full">
              <dd className="text-[12px] text-gray-900 dark:text-white">
                {selected.length === 0 ? (
                  <span className="text-gray-500 dark:text-gray-400">
                    {placeholder}
                  </span>
                ) : (
                  selectedText
                )}
              </dd>
              <ChevronsUpDown className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400 transition-transform group-hover:text-gray-700 dark:group-hover:text-gray-300" />
            </div>
          </div>
        </div>

        {/* Selected Items */}
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selected.map((value) => {
              const option = options.find((opt) => opt.value === value)
              return (
                <div
                  key={value}
                  className={cn(
                    'inline-flex items-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md text-[13px] font-medium shadow-sm',
                    classNames?.list_item
                  )}
                >
                  <span className="truncate max-w-[120px]">{option?.name}</span>
                  {!disabled && (
                    <button
                      className="hover:bg-red-100 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-sm p-0.5 flex items-center justify-center"
                      onClick={(e) => removeSelected(value, e)}
                      aria-label={`Remove ${option?.name}`}
                    >
                      <XMarkIcon className="h-2.5 w-2.5" />
                    </button>
                  )}
                  {disabled && (
                    <LockClosedIcon className="h-2.5 w-2.5 text-gray-400 dark:text-gray-500" />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}
